/**
 * Tempo Core Engine (Main Entry)
 *
 * This is the primary export barrel for the `@magmacomputing/tempo` package.
 * It registers the "batteries-included" modules (Parse, Format, Mutate, Duration, Terms)
 * and exports the initialized `Tempo` class alongside core types and utilities.
 */
import { Tempo } from './tempo.class.js';
import { onRegistryReset, enums, getRuntime } from '#tempo/support';
import { ParseModule } from '#tempo/parse';
import { FormatModule } from '#tempo/format';
import { MutateModule } from '#tempo/mutate';
import { DurationModule } from '#tempo/duration';
import { StandardTerms } from '#tempo/std';
import { defineModule } from './plugin/plugin.util.js';
/**
 * TermsModule provides the standard term definitions for Tempo.
 *
 * This module automatically registers standard temporal terms like "today", "tomorrow",
 * "next week", etc., and ensures they are re-registered on registry resets.
 */
export const TermsModule = defineModule({
    /** Module name identifier */
    name: 'TermsModule',
    /** Installs standard terms into the Tempo class */
    install(TempoClass) {
        getRuntime().modules['TermsModule'] = true;
        onRegistryReset(() => { TempoClass.use(StandardTerms); });
        TempoClass.use(StandardTerms);
    },
});
// Batteries Included: Register standard modules
const core = [ParseModule, FormatModule, MutateModule, DurationModule, TermsModule];
getRuntime().modules['Tempo'] = Tempo;
onRegistryReset(() => {
    getRuntime().modules['Tempo'] = Tempo;
    Tempo.use(core);
});
Tempo.use(core);
const pending = getRuntime().modules['$pendingAutoInstalls'];
if (Array.isArray(pending) && pending.length > 0) {
    Tempo.use(pending);
    pending.length = 0;
}
export { parse, format } from '#tempo/module';
export { enums };
export { TermHook } from '#tempo/support';
export { defineConfig } from './config/config.define.js';
export { Interval } from './interval.index.js';
export * from './tempo.class.js';
export default Tempo;
